import * as THREE from "three";
import { getViewerHandles } from "../runtime/viewerHandles";

/** Circle / mesh toolkit panel wireframe groups. */
export const MESH_TOOLKIT_WIREFRAME_OVERLAY_NAME = "meshToolkitWireframe";

/** Mesh inspector (properties panel) wireframe groups. */
export const MESH_INSPECTOR_WIREFRAME_OVERLAY_NAME = "__meshInspectorWireframe";

function parseWireframeHex(color: string): THREE.Color {
  const raw = (color || "#ff0000").replace("#", "").slice(0, 6);
  const n = parseInt(raw, 16);
  if (!Number.isFinite(n)) return new THREE.Color(0xff0000);
  return new THREE.Color(n);
}

export function clearWireframeOverlaysNamed(scene: THREE.Scene, overlayName: string): void {
  const toRemove: THREE.Object3D[] = [];
  scene.traverse((o) => {
    if (o.name === overlayName) toRemove.push(o);
  });
  toRemove.forEach((o) => {
    o.traverse((n) => {
      const anyN = n as THREE.Mesh;
      if (anyN.geometry) anyN.geometry.dispose?.();
      if (anyN.material) {
        const mats = Array.isArray(anyN.material) ? anyN.material : [anyN.material];
        mats.forEach((m) => m?.dispose?.());
      }
    });
    if (o.parent) o.parent.remove(o);
  });
}

/**
 * Removes then optionally re-adds wireframe overlay groups for the given mesh UUIDs
 * (matches `userData.meshName` on scene meshes).
 */
export function renderMeshWireframeOverlayForMeshNames(
  meshNames: string[],
  options: { visible: boolean; color: string; overlayName: string }
): void {
  const { scene } = getViewerHandles();
  if (!scene) return;
  clearWireframeOverlaysNamed(scene, options.overlayName);
  if (!options.visible || meshNames.length === 0) return;

  const hexColor = parseWireframeHex(options.color);

  const attachForMeshName = (meshName: string) => {
    const candidates: THREE.Mesh[] = [];
    scene.traverse((child: THREE.Object3D) => {
      const mesh = child as THREE.Mesh;
      if (mesh.isMesh && mesh.userData && mesh.userData.meshName === meshName) {
        candidates.push(mesh);
      }
    });
    candidates.forEach((srcMesh) => {
      const overlayGroup = new THREE.Group();
      overlayGroup.name = options.overlayName;

      const overlayMat = new THREE.MeshBasicMaterial({
        color: hexColor,
        wireframe: true,
        transparent: true,
        opacity: 0.9,
        depthTest: true,
        side: THREE.DoubleSide,
      });

      const overlayMesh = new THREE.Mesh(srcMesh.geometry.clone(), overlayMat);
      overlayMesh.matrixAutoUpdate = true;
      overlayMesh.position.copy(srcMesh.position);
      overlayMesh.quaternion.copy(srcMesh.quaternion);
      overlayMesh.scale.copy(srcMesh.scale);
      overlayMesh.renderOrder = (srcMesh.renderOrder || 0) + 1;

      overlayGroup.add(overlayMesh);

      if (srcMesh.parent) srcMesh.parent.add(overlayGroup);
      else scene.add(overlayGroup);
    });
  };

  meshNames.forEach((n) => {
    if (n) attachForMeshName(n);
  });
}
